import { ComponentType, useContext, useEffect } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { includes } from 'lodash';

import { CognitoAuthContext } from 'features/auth';
import { EnumUserRole } from '../graphql';
import { moduleRegistry } from 'lib/modules';
import { readInventoryComponents } from 'lib/modules/inventory-components';
import { ModuleId } from 'lib/modules/module-id.enum';
import { useUserState } from 'providers/user-state-provider';
import { Error, FullscreenLoading } from '@farmshare/ui-components';
import logo from 'assets/images/partners.svg';

interface ModuleProtectedRouteProps {
  component: ComponentType;
  moduleId: string;
  /**
   * A key in the module's `settings` blob that must be `true` as well. For
   * modules whose settings are per-component switches (today: `INVENTORY`), so
   * one module can gate a whole area while individual destinations inside it
   * stay dark. An array means any one of them.
   */
  requiredSetting?: string | string[];
  allowedUserRoles?: EnumUserRole[];
  fallbackPath?: string;
  skipLazyWrapper?: boolean;
}

/**
 * Protected route component that checks both authentication and module access
 * @param root0
 * @param root0.component
 * @param root0.moduleId
 * @param root0.requiredSetting
 * @param root0.allowedUserRoles
 * @param root0.fallbackPath
 * @param root0.skipLazyWrapper
 */
export function ModuleProtectedRoute({
  component,
  moduleId,
  requiredSetting = undefined,
  allowedUserRoles = [],
  fallbackPath = '/',
  skipLazyWrapper = false,
}: ModuleProtectedRouteProps) {
  const { user } = useUserState();
  const cognitoAuth = useContext(CognitoAuthContext);
  const location = useLocation();

  // Load active modules when user or modules change
  useEffect(() => {
    if (user?.active_vendor?.modules && user?.role) {
      moduleRegistry.loadActiveModules(user.active_vendor.modules, user.role);
    }
  }, [user?.active_vendor?.modules, user?.role]);

  // Show loading while checking authentication
  if (cognitoAuth?.isLoading) {
    return <FullscreenLoading />;
  }

  // If not authenticated, redirect to login
  if (!cognitoAuth?.isAuthenticated) {
    const redirectPath = `${location.pathname}${location.search}`;
    return (
      <Navigate
        to={`/login?redirect=${encodeURIComponent(redirectPath)}`}
        replace
      />
    );
  }

  // Wait for the user before deciding: the module list rides on the user
  // record, and the effect above can't have run without it. Readiness is the
  // user, not "the registry has something in it" — a vendor with no modules at
  // all is a legitimate state, and treating it as not-yet-loaded let every
  // module route through.
  if (!user) {
    return <FullscreenLoading />;
  }

  // A plain admin — signed in as staff, not impersonating a vendor — has no
  // active_vendor and therefore no modules. Let them through rather than
  // bouncing them off every module route.
  const isUnscopedAdmin =
    user.role === EnumUserRole.Admin && !user.active_vendor;

  if (!isUnscopedAdmin) {
    // Read the vendor's modules straight off the user rather than the registry:
    // the registry is populated by the effect above, which hasn't run on the
    // first render of a hard load into a module route.
    const vendorModule = user.active_vendor?.modules?.find(
      (m) => m?.moduleType === moduleId,
    );

    if (!vendorModule?.active) {
      return <Navigate to={fallbackPath} replace />;
    }

    // The module is on; check the per-component switch, if this route names one.
    if (requiredSetting) {
      // Inventory's flags go through the same reading as its nav, so a
      // component that depends on stock tracking is off here too when stock
      // tracking is (the nav hid Register, but the URL still loaded it).
      const settings = (
        moduleId === ModuleId.INVENTORY
          ? readInventoryComponents(vendorModule.settings)
          : vendorModule.settings
      ) as Record<string, unknown> | undefined;

      const anyOf = Array.isArray(requiredSetting)
        ? requiredSetting
        : [requiredSetting];
      if (!anyOf.some((key) => settings?.[key] === true)) {
        return <Navigate to={fallbackPath} replace />;
      }
    }
  }

  const Component = component;

  // Ensure that VendorOwners can access Vendor routes
  const effectiveAllowedRoles = [...allowedUserRoles];
  if (
    user?.role === EnumUserRole.VendorOwner &&
    allowedUserRoles.includes(EnumUserRole.Vendor)
  ) {
    effectiveAllowedRoles.push(EnumUserRole.VendorOwner);
  }

  // Check user role
  if (
    user &&
    effectiveAllowedRoles.length > 0 &&
    !includes(effectiveAllowedRoles, user.role) &&
    user.role !== EnumUserRole.Admin
  ) {
    return (
      <Error
        error={{ name: '403', message: 'Access denied.' }}
        logo={
          <img
            src={logo}
            height={100}
            alt="farmshare partners logo"
            className="mt-1 text-body p-3 p-md-0"
          />
        }
      />
    );
  }

  if (skipLazyWrapper) {
    return <Component />;
  }

  return <Component />;
}
